import { Injectable, WritableSignal, inject, signal } from "@angular/core";
import { FiatService } from "@main-services/currency";
import { UserService } from "@main-services/user.service";
import { BehaviorSubject, catchError, combineLatest, EMPTY, map, Observable, of, switchMap, take, tap } from "rxjs";
import { OrdersRemoteService } from "../remotes/orders.remote.service";
import { LimitOrdersService } from "./limit-orders.service";
import { LimitOrderNavigationFacade } from "./navigation.facade";
import { OrderType } from "@gql/offer";
import { LimitOrderQueryParamName, LimitOrderRouteName } from "../interfaces/limit-orders-route.interface";
import { IWidgetAdpOrder } from "../interfaces/limit-orders-widget-adp.model";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import {
	CombinedFollowingWidgetOrderData,
	InputData,
	IFollowingCountData,
	IFFinCount,
	IFollowingCombinedFF
} from "../interfaces/limit-order-following.model";
import { OrdersCountFacade } from "@modules/limit-orders/services/orders-count.facade";
import { resetTimer } from "@helpers/functions";
import { BFFAssetsService } from "@core-services/bff-assets.service";
import { IOrdersList } from "../interfaces/limit-orders.remote.interface";

export const EXPIRE_TIME = 5 * 60 * 1000; // 5 minutes

const initialOrdersValue: CombinedFollowingWidgetOrderData<IWidgetAdpOrder[]> = {
	data: [],
	error: false,
	cached: false,
	expires: resetTimer(EXPIRE_TIME)
};

const initialOrdersCountData: InputData<IFollowingCountData> & IFFinCount = {
	data: { buyOrders: 0, sellOrders: 0, totalOrders: 0, executedOrders: 0, cancelledOrders: 0, max: false },
	ff: { buyEnabled: false, sellEnabled: false, totalEnabled: false },
	error: false,
	cached: false,
	expires: resetTimer(EXPIRE_TIME)
};

@Injectable({ providedIn: "root" })
export class OrdersFollowingWidgetFacade {
	private readonly fiatService = inject(FiatService);
	private readonly ordersService = inject(LimitOrdersService);
	private readonly navigation = inject(LimitOrderNavigationFacade);
	private readonly limitOrdersRemote = inject(OrdersRemoteService);
	private readonly userService = inject(UserService);
	private readonly tradableListService = inject(TradableListService);
	private readonly orderCountFacade = inject(OrdersCountFacade);
	private readonly bff = inject(BFFAssetsService);

	private readonly isOrdersBuyEnabled$: Observable<boolean> = this.ordersService.isBuyLimitEnabledFF$;
	private readonly isOrdersSellEnabled$: Observable<boolean> = this.ordersService.isSellLimitEnabledFF$;
	private readonly isTotalOrdersEnabled$: Observable<boolean> = this.ordersService.isTotalOrdersEnabledFF$;
	readonly limitOrdersCombinationFF$: Observable<IFollowingCombinedFF> = combineLatest({
		buyEnabled: this.isOrdersBuyEnabled$,
		sellEnabled: this.isOrdersSellEnabled$,
		totalEnabled: this.isTotalOrdersEnabled$
	});

	private buyOrdersSignal$: WritableSignal<CombinedFollowingWidgetOrderData<IWidgetAdpOrder[]>> =
		signal(initialOrdersValue);

	private sellOrdersSignal$: WritableSignal<CombinedFollowingWidgetOrderData<IWidgetAdpOrder[]>> =
		signal(initialOrdersValue);

	private allOrdersSignal$: WritableSignal<CombinedFollowingWidgetOrderData<IWidgetAdpOrder[]>> =
		signal(initialOrdersValue);

	ordersCountSignal$: WritableSignal<InputData<IFollowingCountData> & IFFinCount> =
		this.orderCountFacade.ordersCountSignal$;

	private invalidateCacheBuyOrdersSignal$$ = new BehaviorSubject<void>(null);
	private invalidateCacheSellOrdersSignal$$ = new BehaviorSubject<void>(null);
	private invalidateCacheAllOrdersSignal$$ = new BehaviorSubject<void>(null);
	private invalidateCacheCountOrdersSignal$$ = new BehaviorSubject<void>(null);

	constructor() {
		//init the signals for counts
		this.fetchOrdersCountWithFF();
	}

	getOrders$(type: OrderType = null): Observable<CombinedFollowingWidgetOrderData<IWidgetAdpOrder[]>> {
		let selectedInvalidator$$ = this.invalidateCacheAllOrdersSignal$$.asObservable();
		let selectedSignal$ = this.allOrdersSignal$;
		if (type === OrderType.Buy) {
			selectedInvalidator$$ = this.invalidateCacheBuyOrdersSignal$$.asObservable();
			selectedSignal$ = this.buyOrdersSignal$;
		}

		if (type === OrderType.Sell) {
			selectedInvalidator$$ = this.invalidateCacheSellOrdersSignal$$.asObservable();
			selectedSignal$ = this.sellOrdersSignal$;
		}

		return selectedInvalidator$$.pipe(
			switchMap(() => {
				if (selectedSignal$().cached && !this.hasExpired(selectedSignal$().expires)) {
					return of(selectedSignal$());
				}

				const unVerifiedOrUnconfirmed =
					this.userService.isUnverified || this.userService.isWaitingForDocuments();

				return combineLatest({
					orders: this.limitOrdersRemote.fetchOrdersList$({ pagesize$: 5 }, type).pipe(
						take(1),
						switchMap((list) => {
							return this.addGenericBFFToTradableList(list);
						})
					),
					limitOrdersFF: this.limitOrdersCombinationFF$
				}).pipe(
					map(({ orders, limitOrdersFF }) => {
						const input = orders.orders.map((order) => {
							const asset = this.tradableListService.getItemByPid(order.assetId);

							return {
								...order,
								asset: asset,
								fiat: this.fiatService.findByPid(order.fiatId),
								fiatPrecision: asset?.getPrecisionFiat() ?? 0,
								//additional properties
								unVerifiedOrUnconfirmed,
								totalOrdersEnabled: limitOrdersFF.totalEnabled,
								orderType: OrderType.Buy
							};
						});

						selectedSignal$.set({
							data: input,
							error: false,
							cached: true,
							expires: resetTimer(EXPIRE_TIME)
						});

						return selectedSignal$();
					}),
					catchError((error) =>
						of({ data: [], error: true, cached: false, expires: resetTimer(EXPIRE_TIME) })
					)
				);
			})
		);
	}

	private addGenericBFFToTradableList(orders: IOrdersList): Observable<IOrdersList> {
		const filteredList = this.ordersService.filterSecurityAssetListFromOrderListHelper(orders);
		if (filteredList.length === 0) {
			return of(orders);
		}

		return this.ordersService.fetchAssetsViaWithGenericBFF$(filteredList).pipe(
			tap((assets) => {
				this.ordersService.addAssetsToTradableList(assets);
			}),
			map(() => orders)
		);
	}

	fetchOrdersCountWithFF(): void {
		this.invalidateCacheCountOrdersSignal$$
			.asObservable()
			.pipe(
				switchMap(() => {
					return this.limitOrdersCombinationFF$.pipe(
						switchMap((ff) => {
							if (ff.buyEnabled || ff.sellEnabled) {
								if (
									this.ordersCountSignal$().cached &&
									!this.hasExpired(this.ordersCountSignal$().expires)
								) {
									return EMPTY;
								}

								return this.limitOrdersRemote.getOrderCount$().pipe(map((counts) => ({ counts, ff })));
							}

							return EMPTY;
						})
					);
				})
			)
			.subscribe({
				next: ({ counts, ff }) => {
					const { opened, maxOpenOrders, closed, done } = counts;

					const buyOrders = opened.buy;
					const sellOrders = opened.sell;
					const executedOrders = done.buy + done.sell;
					const cancelledOrders = closed.buy + closed.sell;
					const totalOrders = opened.buy + opened.sell;
					const max = totalOrders >= maxOpenOrders;

					this.ordersCountSignal$.set({
						data: { buyOrders, sellOrders, totalOrders, executedOrders, cancelledOrders, max },
						ff,
						error: false,
						cached: true,
						expires: resetTimer(EXPIRE_TIME)
					});
				},
				error: () => {
					this.ordersCountSignal$.set({
						data: initialOrdersCountData.data,
						ff: initialOrdersCountData.ff,
						error: true,
						cached: false,
						expires: resetTimer(EXPIRE_TIME)
					});
				}
			});
	}

	//Invalidate Cache Signals
	private setInvalidateCacheBuyOrdersSignal(): void {
		this.buyOrdersSignal$.update((state) => ({ ...state, cached: false, expires: resetTimer(EXPIRE_TIME) }));
		this.invalidateCacheBuyOrdersSignal$$.next();
	}

	private setInvalidateCacheSellOrdersSignal(): void {
		this.sellOrdersSignal$.update((state) => ({ ...state, cached: false, expires: resetTimer(EXPIRE_TIME) }));
		this.invalidateCacheSellOrdersSignal$$.next();
	}

	private setInvalidateCacheAllOrdersSignal(): void {
		this.allOrdersSignal$.update((state) => ({ ...state, cached: false, expires: resetTimer(EXPIRE_TIME) }));
		this.invalidateCacheAllOrdersSignal$$.next();
	}

	private setInvalidateCacheCountOrdersSignal(): void {
		this.ordersCountSignal$.update((state) => ({ ...state, cached: false, expires: resetTimer(EXPIRE_TIME) }));
		this.invalidateCacheCountOrdersSignal$$.next();
	}

	invalidateAllCache(): void {
		this.setInvalidateCacheBuyOrdersSignal();
		this.setInvalidateCacheSellOrdersSignal();
		this.setInvalidateCacheAllOrdersSignal();
		this.setInvalidateCacheCountOrdersSignal();
	}

	//Navigation
	openAssetSelection(orderType: OrderType): void {
		this.ordersService.openLimitOrderFlow(orderType);
	}

	openGenericOrderFlow(assetId?: string): void {
		this.ordersService.openOrderTypeDecision(assetId);
	}

	goToOrderDetail(orderId: string, orderType: OrderType): void {
		this.navigation.gotoByNavigation(LimitOrderRouteName.LimitOrderDetail, {
			[LimitOrderQueryParamName.OrderId]: orderId,
			[LimitOrderQueryParamName.OrderType]: orderType
		});
	}

	goToLimitOrdersList(assetPid?: string, filterType?: string): void {
		this.navigation.goToOrderListAssetPid(assetPid, filterType);
	}

	//helpers
	hasSellBalance(): boolean {
		return this.ordersService.hasSellBalance();
	}

	private hasExpired(expirationTime: Date): boolean {
		return new Date(Date.now()) >= expirationTime;
	}
}
